/** Circular orbit of sprites around an entity — channeling indicators, shields, charged states. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { ensureSoftCircleTexture, CRISP_SCALE } from '../../ui/CrispUtil';

const TAU = Math.PI * 2;

export interface OrbitalSpritesConfig extends VfxPrimitiveConfig {
  particleCount: number;
  orbitRadius: number;
  angularSpeed: number;
  mode: 'steady' | 'accelerate' | 'spiralIn' | 'spiralOut';
  shimmer: boolean;
  shimmerPeriodMs?: number;
  shimmerAlphaRange?: number;
}

export class OrbitalSprites extends VfxPrimitive<OrbitalSpritesConfig> {
  private sprites!: Phaser.GameObjects.Image[];
  private textureKey!: string;
  private angle = 0;
  private currentRadius: number;
  private currentSpeed: number;
  private playing = false;

  private shimmerTweens: Phaser.Tweens.Tween[] = [];
  private radiusTween: Phaser.Tweens.Tween | null = null;
  private speedTween: Phaser.Tweens.Tween | null = null;

  constructor(scene: Phaser.Scene, config: OrbitalSpritesConfig) {
    super(scene, config);
    this.currentRadius = config.orbitRadius;
    this.currentSpeed = config.angularSpeed;
  }

  protected allocatePool(): void {
    this.sprites = [];
    const crispSize = this.config.textureSize * CRISP_SCALE;
    this.textureKey = ensureSoftCircleTexture(this.scene, crispSize);

    for (let i = 0; i < this.config.particleCount; i++) {
      const img = this.createPoolImage(this.textureKey);
      this.sprites.push(img);
    }
  }

  play(x: number, y: number, depth: number): void {
    this.playing = true;
    this.angle = 0;
    this.currentRadius = this.config.orbitRadius;
    this.currentSpeed = this.config.angularSpeed;

    const count = this.sprites.length;
    for (let i = 0; i < count; i++) {
      const theta = (i * TAU) / count;
      const sx = x + Math.cos(theta) * this.currentRadius;
      const sy = y + Math.sin(theta) * this.currentRadius;
      this.activate(this.sprites[i], sx, sy, depth);
    }

    if (this.config.shimmer) {
      this.startShimmer();
    }
  }

  stop(): void {
    if (!this.playing) return;
    this.playing = false;
    this.killShimmerTweens();
    this.killTransitionTweens();

    const active = this.sprites.filter(s => s.active);
    if (active.length === 0) return;

    // Spiral outward and fade
    const startRadius = this.currentRadius;
    const obj = { r: startRadius, a: 1 };
    this.scene.tweens.add({
      targets: obj,
      r: startRadius * 2,
      a: 0,
      duration: 300,
      ease: 'Quad.easeOut',
      onUpdate: () => {
        for (let i = 0; i < active.length; i++) {
          const theta = this.angle + (i * TAU) / active.length;
          const lastX = active[i].x - Math.cos(theta) * (obj.r - 1);
          const lastY = active[i].y - Math.sin(theta) * (obj.r - 1);
          active[i].setPosition(
            lastX + Math.cos(theta) * obj.r,
            lastY + Math.sin(theta) * obj.r,
          );
          active[i].setAlpha(obj.a);
        }
      },
      onComplete: () => {
        for (const s of active) this.deactivate(s);
      },
    });
  }

  updatePosition(x: number, y: number, depth: number, delta: number): void {
    if (!this.playing) return;

    const dt = delta / 1000;
    this.angle += this.currentSpeed * dt;

    // Mode-specific per-frame changes
    switch (this.config.mode) {
      case 'accelerate':
        this.currentSpeed += this.config.angularSpeed * 0.5 * dt;
        break;
      case 'spiralIn':
        this.currentRadius = Math.max(0, this.currentRadius - 20 * dt);
        break;
      case 'spiralOut':
        this.currentRadius += 20 * dt;
        break;
    }

    const count = this.sprites.length;
    const d = depth + (this.config.depthOffset ?? 0);
    for (let i = 0; i < count; i++) {
      if (!this.sprites[i].active) continue;
      const theta = this.angle + (i * TAU) / count;
      this.sprites[i].setPosition(
        x + Math.cos(theta) * this.currentRadius,
        y + Math.sin(theta) * this.currentRadius,
      ).setDepth(d);
    }
  }

  setRadius(radius: number, transitionMs?: number): void {
    if (this.radiusTween) {
      this.radiusTween.destroy();
      this.radiusTween = null;
    }

    if (!transitionMs || transitionMs <= 0) {
      this.currentRadius = radius;
      return;
    }

    const obj = { value: this.currentRadius };
    this.radiusTween = this.scene.tweens.add({
      targets: obj,
      value: radius,
      duration: transitionMs,
      ease: 'Sine.easeInOut',
      onUpdate: () => { this.currentRadius = obj.value; },
      onComplete: () => { this.radiusTween = null; },
    });
  }

  setSpeed(speed: number, transitionMs?: number): void {
    if (this.speedTween) {
      this.speedTween.destroy();
      this.speedTween = null;
    }

    if (!transitionMs || transitionMs <= 0) {
      this.currentSpeed = speed;
      return;
    }

    const obj = { value: this.currentSpeed };
    this.speedTween = this.scene.tweens.add({
      targets: obj,
      value: speed,
      duration: transitionMs,
      ease: 'Sine.easeInOut',
      onUpdate: () => { this.currentSpeed = obj.value; },
      onComplete: () => { this.speedTween = null; },
    });
  }

  private startShimmer(): void {
    this.killShimmerTweens();
    const range = this.config.shimmerAlphaRange ?? 0.2;
    const period = this.config.shimmerPeriodMs ?? 800;

    for (let i = 0; i < this.sprites.length; i++) {
      const tween = this.scene.tweens.add({
        targets: this.sprites[i],
        alpha: { from: 1 - range, to: 1 },
        duration: period / 2,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: (i * period) / this.sprites.length,
      });
      this.shimmerTweens.push(tween);
    }
  }

  private killShimmerTweens(): void {
    for (const t of this.shimmerTweens) t.destroy();
    this.shimmerTweens.length = 0;
  }

  private killTransitionTweens(): void {
    if (this.radiusTween) {
      this.radiusTween.destroy();
      this.radiusTween = null;
    }
    if (this.speedTween) {
      this.speedTween.destroy();
      this.speedTween = null;
    }
  }
}
